<script lang="ts" setup>
import { flatten } from 'lodash-es';
import { computed, ref } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { MassRetagFormData } from '@modules/mass-retag/types';
import VCard from '@components/card/VCard/VCard.vue';
import { useRestClient } from '@modules/core/services/api';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_MASS_RETAG } from '@modules/mass-retag/constants';
import { useSettingsAffiliateBannerSaveMutation } from '@modules/mass-retag/mutations';
import { EMassRetagFormFields } from '@modules/mass-retag/types';

const { getTranslation } = useI18n();
const { openLink } = useRestClient();

const formData = ref<MassRetagFormData>();

const {
  mutateAsync: sendFile,
  errors,
  clearAllErrors,
  isLoading,
} = useSettingsAffiliateBannerSaveMutation();

const formSchema = computed<FormSchema<MassRetagFormData>>(() => ({
  [EMassRetagFormFields.FILE]: {
    type: EFormInputTypes.SINGLE_FILE,
    label: getTranslation(TRANSLATION_GROUP_MASS_RETAG, 'CHOOSE_FILE').value,
    required: true,
    value: formData.value?.file,
    dragAndDropLabel: getTranslation(TRANSLATION_GROUP_MASS_RETAG, 'DROP_FILE_HERE').value,

    accept: 'text/plain, application/vnd.ms-excel, text/x-csv, application/csv, application/x-csv, text/csv, text/comma-separated-values, text/x-comma-separated-values',
    format: 'csv',
  },
}));

const mappedErrors = computed(() => {
  const fileErrors = errors.value ? flatten(Object.values(errors.value)) : [];
  return {
    ...errors.value,
    [EMassRetagFormFields.FILE]: fileErrors,
  };
});

async function submitHandler(data: MassRetagFormData): Promise<void> {
  formData.value = data;
  const form = new FormData();

  for (const [name, value] of Object.entries(data)) {
    if (value) {
      form.append(name, value);
    }
  }

  try {
    const { link } = await sendFile(form);
    formData.value = undefined;
    openLink(link, { excludeHost: true });
  } catch {}
}
</script>

<template>
  <VCard v-data-qa-auto-id="'MassRetagPage'"
    :title="$getTranslation(TRANSLATION_GROUP_MASS_RETAG, 'HEADER')"
    type="form"
  >
    <VForm
      :schema="formSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_MASS_RETAG, 'SUBMIT')"
      :custom-errors="mappedErrors"
      :is-loading="isLoading"
      @submit="submitHandler($event)"
      @interacted="clearAllErrors"
    />
  </VCard>
</template>
